/**
 * Environment configuration for standalone mode
 * Similar to omnichannel-ui's environment.ts
 */

import type { IEnvironmentConfig } from './types'

export const environments = {
  local: {
    apiHost: 'localhost:9061',
    socketUrl: 'ws://localhost:9061/ws/chat/v2/',
    environment: 'local' as const,
    mode: 'standalone' as const
  },
  staging: {
    apiHost: 'sb-kcc.kido.vn',
    socketUrl: 'wss://sb-kcc.kido.vn/ws/chat/v2/',
    environment: 'staging' as const,
    mode: 'standalone' as const
  },
  pre: {
    apiHost: 'pre-kcc.kido.vn',
    socketUrl: 'wss://pre-kcc.kido.vn/ws/chat/v2/',
    environment: 'pre' as const,
    mode: 'standalone' as const
  },
  production: {
    apiHost: 'kcc.kido.vn',
    socketUrl: 'wss://kcc.kido.vn/ws/chat/v2/',
    environment: 'production' as const,
    mode: 'standalone' as const
  }
}

/**
 * Get environment config based on VITE_ENVIRONMENT
 * Falls back to VITE_SOCKET_URL for backward compatibility
 */
export const getEnvironmentConfig = (): IEnvironmentConfig => {
  // Check if running in embedded mode (will be set by parent app)
  if (typeof window !== 'undefined') {
    const win = window as Window & {
      __CHAT_EMBEDDED_MODE__?: boolean
      __CHAT_CONFIG__?: IEnvironmentConfig
    }
    if (win.__CHAT_EMBEDDED_MODE__ && win.__CHAT_CONFIG__) {
      return win.__CHAT_CONFIG__
    }
  }

  // Standalone mode - use VITE_ENVIRONMENT
  const envFromVar = import.meta.env.VITE_ENVIRONMENT as keyof typeof environments

  if (envFromVar && envFromVar in environments) {
    return environments[envFromVar]
  }

  // Backward compatibility with VITE_SOCKET_URL
  const socketUrl = import.meta.env.VITE_SOCKET_URL
  if (socketUrl) {
    return {
      apiHost: new URL(socketUrl).host,
      socketUrl,
      environment: 'local',
      mode: 'standalone'
    }
  }

  // Default to local
  return environments.local
}

/**
 * Get current environment info
 */
export const getCurrentEnvironment = () => {
  const config = getEnvironmentConfig()
  return {
    ...config,
    isLocal: config.environment === 'local',
    isStaging: config.environment === 'staging',
    isPre: config.environment === 'pre',
    isProduction: config.environment === 'production',
    isStandalone: config.mode === 'standalone',
    isEmbedded: config.mode === 'embedded'
  }
}
